import React, { ReactElement, useState } from "react";

import Link from "@mui/material/Link";

import * as constants from "Common/EVaultAppConstants";
import { PackageDeliveryStatus } from "Common/Enums";
import dateTimeField from "Common/Fields/DateTimeField";
import { getOrgDisplay } from "Common/Utilities";

import ErrorState from "Components/DataTable/ErrorState";
import DetailList from "Components/DetailList";
import DetailRow from "Components/DetailRow";

import { retrieveDeliveryDetails } from "Adapters/Mers/mersDeliveryAdapter";

import MersIdentificationNumber from "../Mers/MersIdentificationNumber";
import {
  DeliveryRecipientStatusValue,
  IDeliveryDetails,
  IDeliveryRecipientDetails,
  IVaultViewClosedLoansSearchResultData,
  VaultViewClosedLoansTabTypes,
} from "../VaultInterfaces";

interface IDeliveryDetailsModal {
  data: IVaultViewClosedLoansSearchResultData;
  keyRoot: string;
  onClose: () => void;
  onShowDeliveryCancelConfirmModal: (response: IVaultViewClosedLoansSearchResultData) => void;
}

export default function DeliveryDetailsModal(props: IDeliveryDetailsModal): ReactElement {
  const { data, keyRoot, onClose } = props;
  const [details, setDetails] = React.useState<IDeliveryDetails | null>(null);
  const [showError, setShowError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const referenceId = data.mersData.deliveryTransaction.referenceId;

  React.useEffect(() => {
    function retrieveDetails() {
      setShowError(false);
      retrieveDeliveryDetails(referenceId).then(
        (data) => {
          setDetails(data);
          setIsLoading(false);
        },
        (error) => {
          setIsLoading(false);
          setShowError(true);
          console.error("Delivery details retrieval error", error);
          console.log(error);
        }
      );
    }
    if (isLoading) {
      retrieveDetails();
    }
  }, [isLoading, referenceId]);

  function handleRetry() {
    setIsLoading(true);
  }

  let cancelDeliveryLink;
  if (data.mersData.mersDeliveryStatus === PackageDeliveryStatus.DeliveryPending) {
    cancelDeliveryLink = (
      <p className="margin-top-xxsmall">
        <span>To abort this delivery, </span>
        <Link className="pseudolink__body" onClick={handleShowDeliveryCancelConfirmation} role="button">
          click here
        </Link>
      </p>
    );
  }

  let completionDate = "Unknown";
  let initiatedDate = "Unknown";
  let initiator = "Unknown";
  const recipients: string[] = [];
  let documents = "Unknown";

  if (details != null) {
    if (data.mersData.mersDeliveryStatus === PackageDeliveryStatus.Delivered) {
      // TODO: Will want to remove this default in the future and use ||
      completionDate = dateTimeField.renderForDisplay(details.completionDate || completionDate);
    } else if (data.mersData.mersDeliveryStatus === PackageDeliveryStatus.DeliveryPending) {
      completionDate = "In progress";
    }

    // TODO: Will want to remove this default in the future and use ||
    initiatedDate = dateTimeField.renderForDisplay(details.initiatedDate || initiatedDate);

    if (details.initiator != null) {
      initiator = getOrgDisplay(details.initiator);
    }

    details.recipientDetails.forEach((recipientDetails: IDeliveryRecipientDetails) => {
      let recipient = getOrgDisplay(recipientDetails.mersOrg);
      const status = DeliveryRecipientStatusValue[recipientDetails.deliveryRecipientStatus];
      recipient += " - " + status.toString();
      recipients.push(recipient);
    });

    documents = details.filename;
  }

  function handleShowDeliveryCancelConfirmation() {
    onClose();
    props.onShowDeliveryCancelConfirmModal(data);
  }

  let content = (
    <div>
      <DetailRow label="Complete" value={completionDate} />
      <DetailRow label="Initiated" value={initiatedDate} />
      <DetailRow label="Loan #" value={data.loanNumber || "Unknown"} />
      <DetailRow
        label="MIN"
        value={
          <MersIdentificationNumber
            className="pseudolink__body"
            keyRoot={keyRoot}
            loan={data}
            loanType={constants.CLOSED_LOANS}
            tabKeyRoot={VaultViewClosedLoansTabTypes.MERSInquiryDetails}
            value={data.min}
          />
        }
      />
      <DetailRow label="Initiator" value={initiator} />
      <DetailRow label="Documents" value={documents} />

      {recipients.length === 0 ? (
        <DetailRow label="Recipients" value="Unknown" />
      ) : (
        <DetailList label="Recipients" values={recipients} />
      )}

      {cancelDeliveryLink}
    </div>
  );

  if (showError) {
    content = <ErrorState message="An error occurred while loading delivery details." onRetry={handleRetry} />;
  }
  return content;
}
